import React from 'react';
import Draggable from 'react-draggable';
import LottiePlayer from './LottiePlayer';
import { authService } from '../services/authService';
import { HTTP_API_URL } from '../config/api';
import './GeminiProModal.css';

export interface GeminiFallbackState {
  status?: string;
  model?: string;
  file_name?: string;
  display_name?: string;
  safe_file_key?: string;
  thumbnail?: string | null;
  thumbnail_url?: string | null;
  full_image_blob?: string | null;
  started_at?: unknown;
}

interface GeminiProModalProps {
  state?: GeminiFallbackState | null;
  onPreviewFullImage: (src: string | null) => void;
}

type LottieAnimationData = Record<string, unknown>;

type AnimationKey = 'globe' | 'globe2' | 'globe3' | 'location';
const GLOBE_VARIANTS: AnimationKey[] = ['globe', 'globe2', 'globe3', 'location'];

const animationCache = new Map<string, LottieAnimationData>();

const loadAnimationFromUrl = async (url: string): Promise<LottieAnimationData> => {
  const cached = animationCache.get(url);
  if (cached) return cached;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Unable to load animation ${url}`);
  }
  const data = await response.json();
  animationCache.set(url, data);
  return data;
};

const loadGlobeAnimation = async (key: AnimationKey): Promise<LottieAnimationData> => {
  switch (key) {
    case 'globe':
      return (await import('../assets/Lottie/Globe.json')).default;
    case 'globe2':
      return loadAnimationFromUrl((await import('../assets/Lottie/Globe2.json?url')).default);
    case 'globe3':
      return loadAnimationFromUrl((await import('../assets/Lottie/Globe3.json?url')).default);
    case 'location':
    default:
      return (await import('../assets/Lottie/Location.json')).default;
  }
};

const loadScrollingText = async (): Promise<LottieAnimationData> =>
  (await import('../assets/Lottie/ScrollingText.json')).default;

export const GeminiProModal: React.FC<GeminiProModalProps> = ({
  state,
  onPreviewFullImage,
}) => {
  const [imageLoading, setImageLoading] = React.useState(false);
  const [imageError, setImageError] = React.useState<string | null>(null);
  const [resolvedUrl, setResolvedUrl] = React.useState<string | null>(null);
  const [selectedAnimationKey, setSelectedAnimationKey] = React.useState<AnimationKey>(() => {
    const idx = Math.floor(Math.random() * GLOBE_VARIANTS.length);
    return GLOBE_VARIANTS[idx];
  });
  const [selectedAnimation, setSelectedAnimation] = React.useState<LottieAnimationData | null>(null);
  const [scrollingAnimation, setScrollingAnimation] = React.useState<LottieAnimationData | null>(null);
  const nodeRef = React.useRef<HTMLDivElement>(null);
  const animationSeed = `${state?.safe_file_key || ''}::${state?.started_at || ''}::${state?.status || ''}`;

  React.useEffect(() => {
    if (!state) {
      return;
    }
    const idx = Math.floor(Math.random() * GLOBE_VARIANTS.length);
    setSelectedAnimationKey(GLOBE_VARIANTS[idx]);
  }, [animationSeed, state]);

  React.useEffect(() => {
    let active = true;
    loadGlobeAnimation(selectedAnimationKey)
      .then((data) => {
        if (active) setSelectedAnimation(data);
      })
      .catch(() => {
        if (active) setSelectedAnimation(null);
      });
    return () => {
      active = false;
    };
  }, [selectedAnimationKey]);

  React.useEffect(() => {
    if (scrollingAnimation) return;
    let active = true;
    loadScrollingText()
      .then((data) => {
        if (active) setScrollingAnimation(data);
      })
      .catch(() => {
        if (active) setScrollingAnimation(null);
      });
    return () => {
      active = false;
    };
  }, [scrollingAnimation]);

  React.useEffect(() => {
    setImageLoading(false);
    setImageError(null);
    setResolvedUrl(null);
  }, [state?.safe_file_key]);

  if (!state || state.status !== 'waiting') {
    return null;
  }

  const thumbSrc =
    state.thumbnail_url ||
    (state.thumbnail && (state.thumbnail.startsWith('data:') ? state.thumbnail : `data:image/jpeg;base64,${state.thumbnail}`)) ||
    null;

  const handleThumbnailClick = async () => {
    if (imageLoading) return;
    if (!thumbSrc && !state.full_image_blob) {
      return;
    }
    if (!state.full_image_blob) {
      onPreviewFullImage(thumbSrc);
      return;
    }
    if (resolvedUrl) {
      onPreviewFullImage(resolvedUrl);
      return;
    }
    try {
      setImageError(null);
      setImageLoading(true);
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      const idToken = await authService.requireAuthToken();
      const response = await fetch(`${HTTP_API_URL}/prompt/image-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ blob_path: state.full_image_blob }),
      });
      if (!response.ok) {
        throw new Error(`Unable to fetch signed URL (${response.status})`);
      }
      const data = await response.json();
      if (!data?.url) {
        throw new Error('Malformed response from image endpoint');
      }
      setResolvedUrl(data.url);
      onPreviewFullImage(data.url);
    } catch (error) {
      console.error('Unable to open Gemini Pro reference image', error);
      setImageError('Unable to load the original image right now.');
    } finally {
      setImageLoading(false);
    }
  };

  return (
    <div className="gemini-pro-overlay">
      <Draggable handle=".gemini-pro-modal__handle" nodeRef={nodeRef}>
        <div
          ref={nodeRef}
          className="gemini-pro-modal"
          role="status"
          aria-live="polite"
        >
          <div className="gemini-pro-modal__header gemini-pro-modal__handle">
            <div>
              <p className="gemini-pro-modal__eyebrow">AI Image Recognition</p>
              <h4>Gathering deeper visual evidence</h4>
            </div>
            <span className="gemini-pro-modal__chip">
              {state.display_name || state.file_name || 'Current photo'}
            </span>
          </div>

          <div className="gemini-pro-modal__body">
            <button
              type="button"
            className="gemini-pro-modal__thumb"
            onClick={handleThumbnailClick}
            disabled={imageLoading}
          >
              {thumbSrc ? (
                <img
                  src={thumbSrc}
                  alt="Currently analysed photo"
                  loading="lazy"
                />
              ) : (
                <span className="gemini-pro-modal__thumb-placeholder">
                  Preview unavailable
                </span>
              )}
              <span className="gemini-pro-modal__thumb-label">
                Tap to open full resolution
              </span>
            </button>

            <div className="gemini-pro-modal__player">
              {selectedAnimation ? (
                <LottiePlayer
                  animationData={selectedAnimation}
                  autoplay
                  loop
                  className="gemini-pro-modal__globe"
                />
              ) : (
                <div className="gemini-pro-modal__globe gemini-pro-modal__globe--fallback">
                  Loading animation…
                </div>
              )}
            </div>
          </div>

          {imageLoading && (
            <p className="gemini-pro-modal__hint">Loading the original image…</p>
          )}
          {imageError && (
            <p className="gemini-pro-modal__hint gemini-pro-modal__hint--error">
              {imageError}
            </p>
          )}

          <div className="gemini-pro-modal__ticker">
            {scrollingAnimation ? (
              <LottiePlayer animationData={scrollingAnimation} autoplay loop />
            ) : (
              <span className="gemini-pro-modal__hint">Loading ticker…</span>
            )}
          </div>
        </div>
      </Draggable>
    </div>
  );
};

export default GeminiProModal;
